import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '@/lib/supabase'
import { ArrowLeft, BookOpen, ChevronRight, Eye } from 'lucide-react'
import EditableText from '@/components/edit/EditableText'

export default function PreviewUnits() {
  const [units, setUnits] = useState<any[]>([])
  const [loading, setLoading] = useState(true)

  const updateUnitField = (unitId: string, field: string, newValue: string) => {
    setUnits(prev => prev.map(u => u.id === unitId ? { ...u, [field]: newValue } : u))
  }

  useEffect(() => {
    loadUnits()
  }, [])

  const loadUnits = async () => {
    const { data } = await supabase
      .from('units')
      .select('id, order_num, title, description')
      .eq('is_active', true)
      .order('order_num', { ascending: true })
    if (data) setUnits(data)
    setLoading(false)
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Link to="/teacher/content" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Vista del curso</h1>
          <div className="flex items-center gap-2 mt-1">
            <Eye className="w-4 h-4 text-amber-600" />
            <span className="text-sm text-amber-600 font-medium">
              Modo previsualización · Solo lectura · Sin registro de progreso
            </span>
          </div>
        </div>
      </div>

      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-sm text-amber-800">
        Puedes ver y hacer todos los ejercicios del curso. Las respuestas no se guardan y no afectan el progreso de los estudiantes.
      </div>

      <div className="space-y-3">
        {units.map((unit) => (
          <Link
            key={unit.id}
            to={`/teacher/preview/unit/${unit.id}`}
            className="flex items-center gap-4 p-5 bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 hover:shadow-md transition-all"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center shrink-0">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-xs text-gray-500 font-medium">Unidad {unit.order_num}</span>
              <EditableText
                as="h3"
                table="units"
                id={unit.id}
                field="title"
                value={unit.title}
                placeholder="(sin título)"
                className="font-semibold text-gray-900"
                onSaved={(v) => updateUnitField(unit.id, 'title', v)}
              />
              <EditableText
                as="p"
                table="units"
                id={unit.id}
                field="description"
                value={unit.description}
                placeholder="(sin descripción)"
                multiline
                className="text-sm text-gray-500 mt-0.5"
                onSaved={(v) => updateUnitField(unit.id, 'description', v)}
              />
            </div>
            <ChevronRight className="w-5 h-5 text-gray-400" />
          </Link>
        ))}

        {units.length === 0 && (
          <div className="text-center py-12 bg-white rounded-xl border">
            <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-3" />
            <p className="text-gray-500">No hay unidades disponibles</p>
          </div>
        )}
      </div>
    </div>
  )
}
